import type { CatalogueEditorialContent } from "@reminder-tracker/backend-contracts";
import {
  catalogueMarkets,
  type BundleVersionDefinition,
  type CatalogueMarket,
} from "@reminder-tracker/domain";
import reviewed from "./scrum-427-reviewed-bundles.json";
import { everydayBundleMemberSemantics } from "./scrum-427-bundle-semantics";

const evidence = [
  {
    id: "scrum-427-d11",
    title: "Approved everyday bundles",
    url: "https://killerapps-1786378266895.atlassian.net/browse/SCRUM-427",
  },
] as const;
const dateLabels = {
  "chosen-task-date": "When would you like to do this?",
  "chosen-review-date": "When would you like to do this?",
  "confirmed-task-date": "What date were you given?",
  "appointment-or-planning-date": "When is the appointment?",
  "bill-or-notice-date": "When is the payment due?",
  "actual-expiry": "When does it expire?",
  "actual-renewal": "When does it renew?",
  "month-expiry-with-chosen-day": "Which day would you like to use?",
} as const;

/** Immutable editorial candidate, not a publication command or runtime dependency on a prototype. */
export function compileEverydayBundleCandidate(): Readonly<{
  bundles: ReadonlyArray<BundleVersionDefinition>;
  presets: ReadonlyArray<CatalogueEditorialContent>;
}> {
  const presets = new Map<string, CatalogueEditorialContent>();
  const bundles = reviewed.bundles.map((bundle): BundleVersionDefinition => {
    if (!catalogueMarkets.includes(bundle.market as CatalogueMarket))
      throw new Error("Unknown reviewed market.");
    const market = bundle.market as CatalogueMarket;
    const variant = bundle.variant === "default" ? "" : `_${bundle.variant}`;
    const members = bundle.sections
      .flatMap((section) => section.tasks)
      .map((task, index) => {
        const semantics = everydayBundleMemberSemantics(`${bundle.id}:${task.id}`);
        const presetId = `rt427_${market.toLowerCase()}_${bundle.id}_${task.id}`;
        const content: CatalogueEditorialContent = {
          presetId,
          equivalenceKey: `rt427_${bundle.id}_${task.id}`,
          marketCode: market,
          contentLocale: "en",
          ...semantics,
          title: task.title,
          aliases: [bundle.title],
          applicability: task.hint,
          dateLabel: dateLabels[semantics.dateKind],
          dateHelper: task.hint,
          helpfulTips: [],
          thingsToConsider:
            bundle.id === "baby"
              ? [
                  "This is your own reminder. Keep children's details, documents and health information outside this app.",
                ]
              : [],
          timingExplanation:
            "A reminder one day before at 09:00 is a starting suggestion. Change it to suit you.",
          interpretation:
            "You choose the applicable date. No legal deadline, clinical interval or next date is calculated.",
          publicationBasis:
            "SCRUM-427-D11 approved life-situation catalogue; market guidance checked 3 October 2026.",
          excludedClaim:
            "Completing a reminder does not confirm an application was accepted or automatically calculate its next date.",
          primaryTimingProfileId: "personal-start",
          planningTimingProfileId: null,
          timingProfiles: [
            {
              id: "personal-start",
              version: 1,
              purpose: "Remember the date you chose.",
              basisBoundary: "A notification suggestion, not a deadline or care schedule.",
              offsets: [{ unit: "days", amount: 1 }],
              localTime: "09:00",
            },
          ],
          sources: bundle.sources.map((source, sourceIndex) => {
            if (source.length !== 2 || source[0] === undefined || source[1] === undefined)
              throw new Error("Malformed reviewed source.");
            return { id: `market-source-${sourceIndex + 1}`, title: source[0], url: source[1] };
          }),
          checkedOn: "2026-10-03",
          nextReviewOn: "2027-01-03",
        };
        const previous = presets.get(presetId);
        if (previous !== undefined && JSON.stringify(previous) !== JSON.stringify(content))
          throw new Error(`Conflicting preset content: ${presetId}`);
        presets.set(presetId, content);
        return {
          memberId: task.id,
          presetId,
          presetVersion: 1,
          order: index + 1,
          startingSelection: task.optional ? ("conditional" as const) : ("additional" as const),
          applicability: task.optional ? ("conditional" as const) : ("broad" as const),
          applicabilityCopy: task.hint,
          conditionalGroup: null,
          dateRelationship: { kind: "independent-confirmed" as const },
        };
      });
    return {
      bundleId: `rt427_${market.toLowerCase()}_${bundle.id}${variant}`,
      version: 1,
      market,
      kind: "life-event",
      state: "draft",
      title:
        bundle.variant === "hdb"
          ? `${bundle.title}: HDB handover`
          : bundle.variant === "dewa"
            ? `${bundle.title}: Dubai DEWA transfer`
            : bundle.title,
      summary: bundle.summary,
      searchAliases: [bundle.title],
      nonCompletenessCopy:
        "Choose only what applies to you. These suggestions are not a complete checklist.",
      availabilityNotes:
        "Confirm requirements with the relevant organisation and use your own dates.",
      evidenceReferences: evidence,
      anchorQuestion: null,
      members,
      sectionLayout: {
        version: "bundle-sections-v1",
        sections: bundle.sections.map((section) => ({
          sectionId: section.id,
          title: section.title,
          summary: section.summary,
          memberIds: section.tasks.map((task) => task.id),
        })),
      },
    };
  });
  return { bundles, presets: [...presets.values()] };
}
